/**
 * Pure helper: derive the visual coverage state for a scene zone from the
 * current contracts + recommendations. Shared by the PixiJS world (to pick each
 * object's insured/uninsured art) and by tests.
 *
 * Rules:
 *   - `missing`  → no contracts in the zone.
 *   - `partial`  → contracts exist but a recommendation still flags the zone.
 *   - `covered`  → at least one contract in the zone, no flagging rec.
 *
 * The `vehicle` zone stays a soft `partial` even without a flagging rec — the
 * demo storyline assumes MTPL only (no casco).
 *
 * `person` is intentionally absent: it reflects auth (login), not contracts, so
 * HomeScene computes it directly.
 */
import type { Contract, Recommendation } from '@shared/schemas'
import type { UploadZoneKey } from './sceneLayout'

export type { UploadZoneKey }
export type ZoneCoverage = 'covered' | 'partial' | 'missing'

export const ZONE_AREAS: Record<UploadZoneKey, readonly string[]> = {
  house: ['property_building', 'property_home'],
  vehicle: ['vehicle_mtpl', 'vehicle_casco'],
  travel: ['travel', 'travel_cancellation', 'travel_luggage'],
  life: ['life', 'accident', 'disability_invalidity', 'critical_illness'],
  pet: ['pet', 'pet_liability'],
}

export function zoneStateFor(
  zone: UploadZoneKey,
  recs: Recommendation[],
  contracts: Contract[],
): ZoneCoverage {
  const areas = ZONE_AREAS[zone]
  const hasContract = contracts.some((c) => areas.includes(c.type))
  const hasRec = recs.some((r) => r.area && areas.includes(r.area))

  if (!hasContract) return 'missing'
  if (hasRec) return 'partial'
  if (zone === 'vehicle') return 'partial'
  return 'covered'
}

/** Stamped accent colour per coverage state (matches pop.* ramp). */
export const ZONE_RING_COLOUR: Record<ZoneCoverage, string> = {
  covered: '#6A8C5F',
  partial: '#D9A441',
  missing: '#C8392B',
}
